import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { TemplatePrompt } from "@/components/TemplatePrompt";
import { MODULES, MODULE_KEYS } from "@/config/modules";
import { useAuth } from "@/context/AuthContext";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line,
} from "recharts";
import { toast } from "sonner";

const Kpi = ({ label, value, icon: Icon, tone = "#002FA7", testid }) => (
  <div className="rounded-md border border-[#E5E7EB] bg-white p-4 hover:shadow-sm transition-shadow" data-testid={testid}>
    <div className="flex items-start justify-between">
      <p className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">{label}</p>
      <Icon className="h-4 w-4" style={{ color: tone }} />
    </div>
    <p className="font-heading text-3xl font-extrabold tracking-tight mt-3" style={{ color: tone }}>{value}</p>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const { allowedModules, features } = useAuth();
  const isAdmin = user?.role === "admin";
  const canSee = (m) => isAdmin || allowedModules.length === 0 || allowedModules.includes(m);
  const hasFeature = (f) => isAdmin || features.length === 0 || features.includes(f);
  const [d, setD] = useState(null);
  const [store, setStore] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e)));
    if (canSee("inventory")) {
      api.get("/store/overview").then(({ data }) => setStore(data))
        .catch((e) => console.warn("sub-store overview unavailable:", e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="dashboard-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-[#111827]">
        Lab Overview
      </h1>
      <p className="text-sm text-[#6B7280] mt-1">
        Welcome back, {user?.name || user?.email} — here is today’s snapshot of the laboratory.
      </p>

      <TemplatePrompt />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {canSee("samples") && <Kpi label="Samples Today" value={d?.samples_today ?? "—"} icon={Icons.TestTubes} testid="kpi-samples-today" />}
        {canSee("tat") && <Kpi label="TAT Breaches" value={d?.tat_breached ?? "—"} icon={Icons.Timer} tone="#C92A2A" testid="kpi-tat-breach" />}
        {canSee("critical_alerts") && <Kpi label="Alerts Pending" value={d?.pending_alerts ?? "—"} icon={Icons.Siren} tone="#B45309" testid="kpi-alerts" />}
        {canSee("inventory") && <Kpi label="Low Stock / Expiry" value={store ? store.reorder_now + store.expired.length : "—"} icon={Icons.Boxes} tone="#2E7D32" testid="kpi-low-stock" />}
      </div>

      {canSee("samples") && <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <div className="rounded-md border border-[#E5E7EB] bg-white p-4">
          <h2 className="text-xs uppercase tracking-[0.1em] text-[#6B7280] mb-4">Daily Sample Volume</h2>
          {(d?.daily_volume || []).length === 0 ? (
            <p className="h-64 flex items-center justify-center text-sm text-[#6B7280]" data-testid="volume-empty">
              No samples logged yet.
            </p>
          ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d?.daily_volume || []}>                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#002FA7" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          )}
        </div>
        <div className="rounded-md border border-[#E5E7EB] bg-white p-4">
          <h2 className="text-xs uppercase tracking-[0.1em] text-[#6B7280] mb-4">Top Tests</h2>
          {(d?.test_distribution || []).length === 0 ? (
            <p className="h-64 flex items-center justify-center text-sm text-[#6B7280]" data-testid="tests-empty">
              No tests logged yet.
            </p>
          ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d?.test_distribution || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-15} height={50} textAnchor="end" />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" fill="#002FA7" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          )}
        </div>
      </div>}

      <h2 className="font-heading text-lg font-bold tracking-tight mt-8 mb-3 text-[#111827]">All Modules</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
        {MODULE_KEYS.filter(canSee).map((k) => {
          const Icon = Icons[MODULES[k].icon] || Icons.Circle;
          return (
            <Link key={k} to={`/m/${k}`} data-testid={`module-card-${k}`}
              className="rounded-md border border-[#E5E7EB] bg-white p-4 hover:border-[#002FA7] hover:shadow-sm transition-all">
              <Icon className="h-5 w-5 text-[#002FA7]" />
              <p className="font-heading font-semibold text-sm mt-3 text-[#111827]">{MODULES[k].label}</p>
              <p className="text-xs text-[#6B7280] mt-1">{d?.counts?.[k] ?? 0} records</p>
            </Link>
          );
        })}
      </div>

      {canSee("inventory") && <>
      <h2 className="font-heading text-lg font-bold tracking-tight mt-8 mb-3 text-[#111827]">Sub-store Alerts</h2>
      <div className="grid md:grid-cols-3 gap-4" data-testid="inventory-alerts">
        {[
          { key: "expired", title: "Expired Lots", tone: "#C92A2A", list: store?.expired, link: "/store/items" },
          { key: "expiring_soon", title: "Expiring in 90 Days", tone: "#B45309", list: store?.expiry_watch, link: "/store/items" },
          { key: "low_stock", title: "At / Below Reorder Level", tone: "#2E7D32", list: store?.reorder_list, link: "/store/indents" },
        ].map((g) => (
          <div key={g.key} className="rounded-md border border-[#E5E7EB] bg-white p-4" data-testid={`alert-group-${g.key}`}>
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">{g.title}</p>
              <span className="text-sm font-heading font-bold" style={{ color: g.tone }}>{g.list?.length ?? 0}</span>
            </div>
            <div className="mt-3 space-y-2">
              {(g.list || []).length === 0 && <p className="text-xs text-[#6B7280]">Nothing to action.</p>}
              {(g.list || []).slice(0, 6).map((it, idx) => (
                <div key={`${g.key}-${idx}`} className="text-sm flex items-center justify-between gap-2">
                  <span className="truncate text-[#111827]">
                    {it.generic_name || it.item}{it.lot_number ? ` · ${it.lot_number}` : ""}
                  </span>
                  <span className="text-xs whitespace-nowrap shrink-0" style={{ color: g.tone }}>
                    {g.key === "low_stock"
                      ? `${it.on_hand ?? 0}/${it.reorder_level ?? 0} ${it.stock_unit || ""}`
                      : it.expiry_date}
                  </span>
                </div>
              ))}
            </div>
            <Link to={g.link} className="inline-block mt-3 text-xs text-[#002FA7] hover:underline"
              data-testid={`alert-link-${g.key}`}>
              {g.key === "low_stock" ? "Plan an indent →" : "Open item master →"}
            </Link>
          </div>
        ))}
      </div>

      </>}

      {canSee("critical_alerts") && <>
      <h2 className="font-heading text-lg font-bold tracking-tight mt-8 mb-3 text-[#111827]">Recent Critical Alerts</h2>
      <div className="rounded-md border border-[#E5E7EB] bg-white divide-y divide-[#E5E7EB]" data-testid="recent-alerts">
        {(d?.recent_alerts || []).length === 0 && (
          <p className="p-4 text-sm text-[#6B7280]">No critical alerts logged yet.</p>
        )}
        {(d?.recent_alerts || []).map((a) => (
          <div key={a.record_id} className="p-3 flex items-center justify-between gap-3 text-sm">
            <span className="font-medium text-[#111827]">{a.critical_test || "—"}</span>
            <span className="text-[#C92A2A] font-medium">{a.value} {a.unit || ""}</span>
            <span className="text-[#6B7280] truncate">{a.patient_name || a.sample_id || ""}</span>
            <span className="text-xs uppercase tracking-[0.08em] text-[#6B7280]">{a.notified || "Pending"}</span>
          </div>
        ))}
      </div>
      </>}
    </div>
  );
}
